import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Capabilities } from '@cloudsign/shared';
import { ApiClientError, api, setAuthFailureListener } from './api';
import { STAGE_PATH, getAuthState, setAuthState, useAuthState } from './authState';
import { applyBranding } from './branding';
import { t } from './i18n';
import { Spinner } from '../components/ui';
import { ErrorPanel } from '../components/ErrorPanel';

export const CAPABILITIES_KEY = ['capabilities'] as const;

export function useCapabilitiesQuery() {
  return useQuery({
    queryKey: CAPABILITIES_KEY,
    queryFn: () => api.get('/me/capabilities', { schema: Capabilities, quiet401: true }),
    retry: false,
    staleTime: 60_000,
  });
}

/** Capabilities for pages that sit inside <RequireSession>, so they are always loaded. */
export function useCaps(): Capabilities {
  const q = useCapabilitiesQuery();
  if (!q.data) throw new Error('useCaps used outside a signed-in page');
  return q.data;
}

export function useCan(permission: string | null | undefined): boolean {
  const q = useCapabilitiesQuery();
  if (!permission) return true;
  return q.data?.permissions.includes(permission) ?? false;
}

export function homePath(area: 'tenant' | 'platform' | 'building'): string {
  // 'building' (ADR 0034): the building area's pages are task W-SR3's.
  return area === 'platform' ? '/platform/tenants' : area === 'building' ? '/building' : '/';
}

/** Sends people back to /login (remembering where they were) whenever the server says 401 mid-session. */
export function AuthFailureHandler() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const location = useLocation();
  useEffect(() => {
    setAuthFailureListener(() => {
      setAuthState(null);
      qc.removeQueries();
      const here = location.pathname + location.search;
      const next = here.startsWith('/login') ? '' : `?next=${encodeURIComponent(here)}`;
      void navigate(`/login${next}`, { replace: true });
    });
    return () => setAuthFailureListener(null);
  }, [navigate, qc, location.pathname, location.search]);
  return null;
}

type Area = 'tenant' | 'platform' | 'building' | 'any';

/**
 * Guards a page: needs a completed login in the right area. Sends unfinished logins to their next
 * step, signed-out people to /login, and people in the other area to their own home.
 */
export function RequireSession({ area, permission, children }: { area: Area; permission?: string | null; children: ReactNode }) {
  const q = useCapabilitiesQuery();
  const auth = useAuthState();
  const location = useLocation();

  useEffect(() => {
    if (q.data) applyBranding(q.data.tenant?.branding ?? null);
  }, [q.data]);

  if (q.isPending) return <Spinner />;
  if (q.isError) {
    const err = q.error;
    if (err instanceof ApiClientError && err.status === 401) {
      const stage = auth?.stage;
      if (stage && stage !== 'complete') return <Navigate to={STAGE_PATH[stage]} replace />;
      const here = location.pathname + location.search;
      return <Navigate to={`/login?next=${encodeURIComponent(here)}`} replace />;
    }
    return (
      <ErrorPanel error={err} onRetry={() => void q.refetch()} />
    );
  }
  const caps = q.data;
  if (area !== 'any' && caps.area !== area) return <Navigate to={homePath(caps.area)} replace />;
  if (permission && !caps.permissions.includes(permission)) {
    return (
      <div role="alert" className="rounded-md border border-red-300 bg-red-50 p-4 text-red-900">
        {t('error.forbidden')}
      </div>
    );
  }
  return <>{children}</>;
}

/** The login step pages only make sense at one stage. Anything else goes back to /login. */
export function RequireStage({ stage, children }: { stage: 'mfa' | 'mfa_setup' | 'choose_tenant' | 'password_change'; children: ReactNode }) {
  const auth = useAuthState();
  const current = auth ?? getAuthState();
  if (current?.stage === stage) return <>{children}</>;
  if (current?.stage === 'complete') return <Navigate to="/" replace />;
  if (current) return <Navigate to={STAGE_PATH[current.stage]} replace />;
  return <Navigate to="/login" replace />;
}
